/*
 * scarlett-price-slider.js — dependency-free dual-handle price slider, ported from the Neto reskin
 * (.hslider) and adapted to Shopify Dawn's facet system.
 *
 * The slider previews lo/hi as the user drags/types; on COMMIT (Apply, Enter, or Clear) it writes
 * the values into Dawn's hidden price inputs (filter.v.price.gte / filter.v.price.lte) in the
 * sibling <price-range>, then dispatches a bubbling `input` event. <facet-filters-form> hears that
 * event and runs its debounced AJAX re-render — so URL state, validation and section reload are all
 * Dawn's, unchanged. Whole-dollar granularity (matches Neto). Pointer + keyboard operable.
 *
 * Re-init: when another filter changes, Dawn replaces the price <details> innerHTML, which re-creates
 * this custom element → connectedCallback re-inits from the freshly server-rendered gte/lte values.
 * A price-only change does NOT replace this element, so its JS state simply persists.
 */
(function () {
  function money(sym, n) {
    try {
      return sym + Number(n).toLocaleString(document.documentElement.lang || 'en-AU');
    } catch (e) {
      return sym + n;
    }
  }

  class ScarlettPriceSlider extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      var sl = this.querySelector('.hslider');
      if (!sl) return;
      this._inited = true;
      try {
        this.initSlider(sl);
      } catch (e) {
        /* never let a slider error break the facet rail */
      }
    }

    initSlider(sl) {
      var sym = sl.getAttribute('data-cur') || '$';

      // Dawn's hidden, form-bound price inputs live in the sibling <price-range>.
      var scope =
        this.closest('.facets__display-vertical') ||
        this.parentElement ||
        this.closest('.js-filter') ||
        document;
      var gte = scope.querySelector('price-range input[name$="price.gte"]');
      var lte = scope.querySelector('price-range input[name$="price.lte"]');

      var rail = sl.querySelector('.hslider-rail');
      var fill = sl.querySelector('.hslider-fill');
      var loH = sl.querySelector('.hs-lo');
      var hiH = sl.querySelector('.hs-hi');
      var lbl = sl.querySelector('.hs-lbl');
      var apply = sl.querySelector('.hslider-apply');
      var clear = sl.querySelector('.hslider-clear');
      var inLo = sl.querySelector('.hs-min');
      var inHi = sl.querySelector('.hs-max');

      var min = parseInt(sl.getAttribute('data-min'), 10);
      var max = parseInt(sl.getAttribute('data-max'), 10);
      if (!rail || !loH || !hiH || isNaN(min) || isNaN(max) || max <= min) return;

      var span = max - min;
      // Keyboard step: span/100 capped at $10 so the full catalogue span stays fine-grained.
      var step = Math.min(10, Math.max(1, Math.round(span / 100)));

      var lo = min;
      var hi = max;
      var dLo = parseInt(sl.getAttribute('data-lo'), 10);
      var dHi = parseInt(sl.getAttribute('data-hi'), 10);
      if (!isNaN(dLo)) lo = Math.max(min, Math.min(max, dLo));
      if (!isNaN(dHi)) hi = Math.max(min, Math.min(max, dHi));
      if (lo > hi) {
        lo = min;
        hi = max;
      }

      function pct(v) {
        return ((v - min) / span) * 100;
      }

      function render() {
        loH.style.left = pct(lo) + '%';
        hiH.style.left = pct(hi) + '%';
        if (fill) {
          fill.style.left = pct(lo) + '%';
          fill.style.width = pct(hi) - pct(lo) + '%';
        }
        if (lbl) lbl.textContent = money(sym, lo) + ' to ' + money(sym, hi);
        if (inLo && document.activeElement !== inLo) inLo.value = lo;
        if (inHi && document.activeElement !== inHi) inHi.value = hi;
        loH.setAttribute('aria-valuenow', lo);
        hiH.setAttribute('aria-valuenow', hi);
        loH.setAttribute('aria-valuetext', money(sym, lo));
        hiH.setAttribute('aria-valuetext', money(sym, hi));
      }

      function commit() {
        // Empty string => "no bound" (clears that side of the filter).
        if (gte) gte.value = lo > min ? lo : '';
        if (lte) lte.value = hi < max ? hi : '';
        // change → Dawn's PriceRange validates; input (bubbling) → debounced facet AJAX submit.
        if (gte) gte.dispatchEvent(new Event('change', { bubbles: true }));
        if (lte) lte.dispatchEvent(new Event('change', { bubbles: true }));
        if (gte) gte.dispatchEvent(new Event('input', { bubbles: true }));
        else if (lte) lte.dispatchEvent(new Event('input', { bubbles: true }));
      }

      function clampTyped(inp, isLo) {
        if (inp.value.trim() === '') {
          // Empty box = "no bound" on that side (clear it), not "leave the old value".
          if (isLo) lo = min;
          else hi = max;
          return;
        }
        var v = parseInt(inp.value, 10);
        if (isNaN(v)) return;
        v = Math.max(min, Math.min(max, v));
        if (isLo) lo = Math.min(v, hi);
        else hi = Math.max(v, lo);
      }

      function bindInput(inp, isLo) {
        if (!inp) return;
        // The Min/Max boxes are unnamed <input>s INSIDE <form id="FacetFiltersForm">, whose
        // 'input' listener debounce-submits. Stop keystrokes from bubbling so typing never fires
        // a stale-bounds AJAX re-render — the slider commits ONLY on Apply / Enter / Clear.
        inp.addEventListener('input', function (ev) {
          ev.stopPropagation();
        });
        inp.addEventListener('change', function () {
          clampTyped(inp, isLo);
          render();
          inp.value = isLo ? lo : hi; // snap a still-focused out-of-range entry to the clamped value
        });
        inp.addEventListener('keydown', function (ev) {
          if (ev.key === 'Enter') {
            ev.preventDefault();
            clampTyped(inp, isLo);
            render();
            commit();
          }
        });
      }
      bindInput(inLo, true);
      bindInput(inHi, false);

      function valAt(clientX) {
        var r = rail.getBoundingClientRect();
        if (!r.width) return min;
        var f = (clientX - r.left) / r.width;
        f = Math.max(0, Math.min(1, f));
        return Math.round(min + f * span);
      }

      function makeDraggable(handle, isLo) {
        handle.style.touchAction = 'none';
        function onMove(ev) {
          var v = valAt(ev.clientX);
          if (isLo) lo = Math.min(v, hi);
          else hi = Math.max(v, lo);
          render();
        }
        function onUp() {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
        }
        handle.addEventListener('pointerdown', function (ev) {
          ev.preventDefault();
          handle.focus();
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
        });
        var pageStep = Math.max(step, Math.round(span / 10));
        handle.addEventListener('keydown', function (ev) {
          var k = ev.key;
          if (k === 'ArrowLeft' || k === 'ArrowDown') {
            if (isLo) lo = Math.max(min, lo - step);
            else hi = Math.max(lo, hi - step);
            render();
            ev.preventDefault();
          } else if (k === 'ArrowRight' || k === 'ArrowUp') {
            if (isLo) lo = Math.min(hi, lo + step);
            else hi = Math.min(max, hi + step);
            render();
            ev.preventDefault();
          } else if (k === 'PageDown') {
            if (isLo) lo = Math.max(min, lo - pageStep);
            else hi = Math.max(lo, hi - pageStep);
            render();
            ev.preventDefault();
          } else if (k === 'PageUp') {
            if (isLo) lo = Math.min(hi, lo + pageStep);
            else hi = Math.min(max, hi + pageStep);
            render();
            ev.preventDefault();
          } else if (k === 'Home') {
            if (isLo) lo = min;
            else hi = lo;
            render();
            ev.preventDefault();
          } else if (k === 'End') {
            if (isLo) lo = hi;
            else hi = max;
            render();
            ev.preventDefault();
          } else if (k === 'Enter') {
            commit();
          }
        });
      }
      makeDraggable(loH, true);
      makeDraggable(hiH, false);

      if (apply) apply.addEventListener('click', commit);
      if (clear)
        clear.addEventListener('click', function () {
          lo = min;
          hi = max;
          render();
          commit();
        });

      render();
      sl.classList.add('is-ready');
      // Only hide Dawn's native From/To row once the slider is actually live — so a JS failure /
      // unsupported browser leaves the native price inputs usable instead of a dead control.
      var fd = this.closest('.facets__display-vertical');
      if (fd) fd.classList.add('scarlett-slider-ready');
    }
  }

  if (!customElements.get('scarlett-price-slider')) {
    customElements.define('scarlett-price-slider', ScarlettPriceSlider);
  }
})();
